import { AbTest } from '../models/AbTest';
import { AbTestVariant } from '../models/AbTestVariant';
import { AbTestWidget } from '../models/AbTestWidget';

/**
 * Normalizes externalId in abTestWidgets from string to number.
 * This handles the case where MySQL BIGINT values are serialized as strings in JSON responses.
 * Only normalizes externalId in ab_test_widgets table (not other externalId fields).
 *
 * @param abTest - The AbTest object to normalize
 * @returns A new AbTest object with externalId in widgets converted to numbers
 */
export function normalizeAbTestData(abTest: AbTest): AbTest {
  if (!abTest) return abTest;

  return {
    ...abTest,
    abTestVariants: abTest.abTestVariants?.map(normalizeAbTestVariant) || [],
  };
}

/**
 * Normalizes externalId in AbTestVariant widgets
 */
function normalizeAbTestVariant(variant: AbTestVariant): AbTestVariant {
  if (!variant) return variant;

  return {
    ...variant,
    abTestWidgets: variant.abTestWidgets?.map(normalizeAbTestWidget) || [],
  };
}

/**
 * Normalizes externalId in AbTestWidget (from ab_test_widgets table)
 */
function normalizeAbTestWidget(widget: AbTestWidget): AbTestWidget {
  if (!widget) return widget;

  return {
    ...widget,
    // Convert externalId from string to number if needed (handles BIGINT serialization)
    externalId: typeof widget.externalId === 'string' ? Number(widget.externalId) : widget.externalId,
  };
}

/**
 * Normalizes an array of AbTest objects
 */
export function normalizeAbTestDataArray(abTests: AbTest[]): AbTest[] {
  if (!Array.isArray(abTests)) return abTests;
  return abTests.map(normalizeAbTestData);
}
